'use client';
import React from 'react';
import { usePathname } from 'next/navigation';

import { AnnouncementBarResponseTypes } from '@src/graphql/queries.types';
import { GQL_FETCH_ANNOUNCEMENT_ENTRIES } from '@src/graphql/queries';
import { IS_PREVIEW, PAGE_LOCALES } from '@src/typescriptGlobals/constants';

import RenderBodyCopy from '../RenderBodyCopy';

import useEbookSlideAnimate from '@src/hooks/useEbookSlideAnimate';
import fetchContentfulProxy from '@src/utils/fetchContentfulProxy';

import { AnnouncementBarPropsTypes, AnnouncementTypes } from './AnnouncementBar.types';

const AnnouncementBar: React.FC<AnnouncementBarPropsTypes> = ({ params: { locale } }) => {
  const pathname = usePathname();
  const ebookSlideRef = useEbookSlideAnimate();
  const [eBooks, setEBooks] = React.useState<AnnouncementTypes>();

  React.useEffect(() => {
    fetchContentfulProxy<AnnouncementBarResponseTypes>({
      query: GQL_FETCH_ANNOUNCEMENT_ENTRIES?.loc?.source?.body ?? '',
      variables: {
        locale: PAGE_LOCALES.includes(locale) ? locale?.toUpperCase() : 'EN',
        preview: IS_PREVIEW,
      },
    }).then((res) => setEBooks(res.eBooks?.items?.[0]));
  }, []);

  const multipleEbook = eBooks?.topicCollection?.items?.length! > 1;
  const pathNameList = ['solutions', 'pathologies', 'clinicians', 'products'];
  const productList = [
    'airbands',
    'forcedecks',
    'humantrak',
    'nordbord',
    'smartspeed',
    'valdhub',
    'dynamo',
    'forceframe',
    'msked',
    'scoreit',
    'telehab',
    'movehealth',
    'norms',
  ];

  const whiteBar =
    pathNameList.some((name) => pathname.includes(name)) &&
    !productList.some((name) => pathname.includes(name));

  return (
    <div
      ref={ebookSlideRef}
      id='announcement-bar'
      className={`text-white shadow-lg invisible absolute top-[7.4rem] z-[100] h-[5rem] w-full items-center justify-center gap-[0.4rem] overflow-hidden bg-primary py-[1rem] text-center text-[1.6rem] font-[500] text-body-copy--light sm:flex sm:h-[auto] [&_*_p]:text-left [&_*_p]:!leading-[1.6rem] ${whiteBar ? '!bg-[#EFEFEF] text-primary' : 'bg-primary text-body-copy--light'}`}
    >
      {eBooks?.title && (
        <p
          className={`self-center text-center leading-[2rem] ${multipleEbook ? 'sm:ml-[-24rem]' : ''}`}
          style={{
            textShadow:
              'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
          }}
        >
          {eBooks?.title}
        </p>
      )}

      <div
        className={`relative flex items-center text-center`}
        style={{
          textShadow:
            'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
        }}
      >
        {eBooks?.topicCollection?.items?.map((eBook) => {
          return (
            <RenderBodyCopy
              key={eBook?.sys?.id}
              bodyCopy={eBook?.bodyCopy?.json?.content}
              className={`top-[1.4rem] flex items-center justify-center text-body-copy--light underline hover:text-[#ECEAE8] sm:left-[unset] sm:top-[unset] sm:justify-start [&>p>a]:text-[1.6rem] [&>p>a]:!text-body-copy--light ${whiteBar ? '[&>p>a]:!hover:text-primary--v2 [&>p>a]:!text-primary' : 'text-body-copy--light hover:text-[#ECEAE8]'} ${multipleEbook ? 'invisible absolute w-[100%] sm:w-[45rem]' : 'mt-[1rem] w-[100%] sm:mt-[unset] md:w-[unset] [&>p]:!w-[100%] [&>p]:!text-center md:[&>p]:!w-[unset]'}`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default AnnouncementBar;
